import { apiClient, LONG_TIMEOUT_MS } from './client'
import type {
  ConversationDTO,
  ConversationListResponseDTO,
  ConversationMessageCreateResponseDTO,
  ConversationMessagesResponseDTO,
  UUID,
} from './types'

/**
 * Conversation API surface. Identity (user/org/tenant) is derived server-side
 * from the session cookie — the client never sends user_id/org_id.
 */

export interface CreateConversationPayload {
  /** Sent for backend validation; service overrides with identity.tenant_id. */
  tenant_id: string
  title?: string
  scope_entity_type?: string | null
  scope_entity_id?: string | null
  scope_group_type?: string | null
  scope_group_id?: string | null
  scope_document_id?: string | null
}

export interface PostMessagePayload {
  content: string
  top_k?: number
  scope_entity_type?: string | null
  scope_entity_id?: string | null
  scope_group_type?: string | null
  scope_group_id?: string | null
  scope_document_id?: string | null
  /**
   * Step 8.1 — Reference Selection for Queries: opcional. Lista de
   * ``document_id``s da Biblioteca de Referência que devem complementar
   * esta pergunta. Backend valida tenant + ``document_kind=reference_document``;
   * omitir ou enviar vazio mantém o comportamento legado (somente escopo).
   */
  reference_document_ids?: string[]
  /**
   * Step 8.3 — Multi-document Case Scope: opcional. Lista de
   * ``document_id``s de **case_documents** que formam o escopo primário
   * desta pergunta. Backend valida tenant + ``document_kind=case_document``;
   * quando presente e não-vazio, prevalece sobre os scope_* para retrieval.
   * Omitir ou enviar vazio mantém o caminho single-doc (scope_*) legado.
   */
  case_document_ids?: string[]
}

export interface UpdateConversationPayload {
  title?: string
  pinned?: boolean
  archived?: boolean
  deleted?: boolean
}

export const conversationsApi = {
  list: (signal?: AbortSignal) =>
    apiClient.get<ConversationListResponseDTO>(
      '/api/v1/conversations',
      undefined,
      signal,
    ),

  create: (payload: CreateConversationPayload) =>
    apiClient.post<ConversationDTO>('/api/v1/conversations', payload),

  getMessages: (conversationId: UUID, signal?: AbortSignal) =>
    apiClient.get<ConversationMessagesResponseDTO>(
      `/api/v1/conversations/${conversationId}/messages`,
      undefined,
      signal,
    ),

  postMessage: (conversationId: UUID, payload: PostMessagePayload) =>
    // RAG retrieval + LLM generation runs much longer than a CRUD call. Use
    // the extended ceiling so a real answer is never cut, while still bounding
    // a stalled request so the composer can't stay "Enviando…" forever.
    apiClient.post<ConversationMessageCreateResponseDTO>(
      `/api/v1/conversations/${conversationId}/messages`,
      payload,
      { timeoutMs: LONG_TIMEOUT_MS },
    ),

  patch: (conversationId: UUID, payload: UpdateConversationPayload) =>
    apiClient.patch<ConversationDTO>(
      `/api/v1/conversations/${conversationId}`,
      payload,
    ),

  remove: (conversationId: UUID) =>
    apiClient.del<ConversationDTO>(
      `/api/v1/conversations/${conversationId}`,
    ),
}
